import Multiselect from "multiselect-react-dropdown";
import { useMemo } from "react";
import { TApplicationField } from "../../hooks/application-fields.hooks";
import { TPopulatedForm } from "../../hooks/applications.hooks";
import { useReviewOrderedFields } from "../../hooks/field-sequence.hooks";

/**
 * Field uids a reviewer cannot send back for correction. Same list the
 * review-stage screens leave out of their "Select Change Fields" picker, and
 * the same one the backend drops if it is sent anyway.
 */
const NON_RETURNABLE_FIELD_UIDS = [
  "noc_number",
  "noc_date",
  "noc_type",
  "name_of_service",
];

/**
 * True when both lists hold the same field ids, in any order. The Final
 * Review screens use it to tell whether the reviewer's current selection has
 * been saved yet - Return acts on the saved selection, not the one on screen.
 */
export function isSameFieldSelection(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const inB = new Set(b);
  return a.every((id) => inB.has(id));
}

/**
 * The "which form fields must the applicant correct" picker shown on the
 * Level 2 / Level 3 Final Review screen of an Offline Tentative application.
 * That is the only application a Final Review can send back to the applicant,
 * so the picker is rendered only for it; the caller decides when.
 *
 * It lists every active field of the application's form steps. The selection
 * is held by the caller and saved together with the reviewer's remark.
 *
 * Multiselect only reads `selectedValues` when it mounts, so the caller gives
 * this component a `key` that changes once the saved review has loaded.
 */
export function OfflineTentativeReturnFields({
  steps,
  selectedFieldIds,
  onChange,
  disabled,
}: {
  steps?: TPopulatedForm[];
  selectedFieldIds: number[];
  onChange: (fieldIds: number[]) => void;
  disabled?: boolean;
}) {
  /**
   * The options follow the Admin's Field Sequence, the same order as the
   * application details list (see selectableFields below).
   * BEFORE: const selectableFields = useMemo(() => { ... }, [steps]);
   * was used as is, always in the form's default order.
   */
  const unorderedFields = useMemo(() => {
    const seen = new Set<number>();
    return (steps || [])
      .flatMap((step) => step.fields || [])
      .filter((field) => {
        if (!field?.active || NON_RETURNABLE_FIELD_UIDS.includes(field.uid)) {
          return false;
        }
        if (seen.has(field.id)) return false;
        seen.add(field.id);
        return true;
      });
  }, [steps]);
  const selectableFields = useReviewOrderedFields(unorderedFields, steps);

  const selectedFields = useMemo(
    () => selectableFields.filter((field) => selectedFieldIds.includes(field.id)),
    [selectableFields, selectedFieldIds]
  );

  return (
    <div className="mt-3">
      <p className="text-sm md:text-base font-bold mb-2">
        Fields to be corrected by the applicant
      </p>
      <Multiselect
        disable={disabled}
        style={{
          multiselectContainer: {
            border: "1px solid #dee2e6",
            "border-radius": "8px",
          },
        }}
        selectedValues={selectedFields}
        placeholder="Select Change Fields"
        options={selectableFields}
        onSelect={(list: TApplicationField[]) => onChange(list.map((e) => e.id))}
        onRemove={(list: TApplicationField[]) => onChange(list.map((e) => e.id))}
        displayValue="label"
      />
      <p className="text-xs text-gray-500 mt-1">
        To return this application, mark a document as not accepted or select a
        field here, then Save before pressing Return.
      </p>
    </div>
  );
}
